import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import DashboardLayout from "../../layouts/DashboardLayout";
import UpgradeBusinessPlus from "../../components/UpgradeBusinessPlus";
import PageHeader from "../../components/PageHeader";
import Loading from "../../components/Loading";
import {GET_SECRET_KEYS} from "../../utils/graphql";
import {apollo} from "../../utils/apollo";
import {useQuery} from "react-query";
import {READONLY_MERCHANT} from "../../utils/env";

function Setting() {
    const {t} = useTranslation();

    return (
        <DashboardLayout>
            {/*<UpgradeBusinessPlus/>*/}
            <PageHeader
                title={t('_.settings')}
                description={t('_.settings_description', {defaultValue: 'Manage your account integration and API keys'})}
            />

            <div className="mt-8">
                <SecretKeys/>
            </div>
        </DashboardLayout>
    );
}

function SecretKeys() {
    const {t} = useTranslation();

    const fetchSecretKeys = useQuery({
        queryKey: 'secret_keys',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_SECRET_KEYS,
                variables: {},
            });
            return data?.getSecretKeys?.data;
        },
        keepPreviousData: false,
        enabled: !READONLY_MERCHANT,
    });

    const secretKeys = useMemo(
        () => (fetchSecretKeys?.isSuccess ? fetchSecretKeys?.data : null),
        [fetchSecretKeys]
    );

    if (READONLY_MERCHANT) return null;

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {fetchSecretKeys.isLoading && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center">
                    <Loading message={t('_.loading')} fullHeight={false} />
                </div>
            )}

            {fetchSecretKeys.isError && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-10 text-center">
                    <p className="text-red-500 font-bold">{t('_.something_went_wrong')}</p>
                </div>
            )}

            {fetchSecretKeys.isSuccess && (!secretKeys || secretKeys.length === 0) && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-10 text-center">
                    <p className="text-gray-500 font-bold">{t('_.no_data')}</p>
                </div>
            )}

            {fetchSecretKeys.isSuccess && secretKeys && secretKeys.length > 0 && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-8 py-6 border-b border-gray-100/50 bg-white/40">
                        <h3 className="text-xl font-bold text-gray-900">{t('_.api_secret_keys', {defaultValue: 'API Secret Keys'})}</h3>
                        <p className="text-sm text-gray-600 mt-1">{t('_.api_secret_keys_desc', {defaultValue: 'Use these keys to authenticate your integration. Keep them secure.'})}</p>
                    </div>
                    <div className="p-0">
                        {secretKeys.map((secretKey, index) => (
                            <div key={secretKey.apiKey} className="px-8 py-8 border-t border-gray-100/60 first:border-t-0">
                                <div className="flex flex-col gap-4">
                                    {/* Key name + channel row */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                        {secretKey.name && (
                                            <span className="text-sm font-bold text-gray-800">{secretKey.name}</span>
                                        )}
                                        {secretKey.channel && (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-cyan-50 text-cyan-700 border border-cyan-100">
                                                {secretKey.channel}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{t('_.api_key', {defaultValue: 'API Key'})}</p>
                                            <ApiKeyField value={secretKey.apiKey} />
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="text-right hidden sm:block">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">{t('_.mode', {defaultValue: 'Mode'})}</p>
                                                <span className={`inline-block mt-0.5 px-3 py-1 rounded-full text-[10px] font-extrabold border shadow-sm ${secretKey.testMode === '1' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100'}`}>
                                                    {secretKey.testMode === '1' ? t('_.test', {defaultValue: 'Test'}) : t('_.live', {defaultValue: 'Live'})}
                                                </span>
                                            </div>
                                            <div className="text-right hidden sm:block">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">{t('_.created_at', {defaultValue: 'Created At'})}</p>
                                                <p className="text-xs text-gray-700 font-bold mt-0.5">{secretKey.createdAt}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4 sm:hidden pt-2 border-t border-gray-50">
                                        <div className="flex-1">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">{t('_.mode', {defaultValue: 'Mode'})}</p>
                                            <span className={`inline-block mt-0.5 px-3 py-1 rounded-full text-[10px] font-extrabold border ${secretKey.testMode === '1' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100'}`}>
                                                {secretKey.testMode === '1' ? t('_.test', {defaultValue: 'Test'}) : t('_.live', {defaultValue: 'Live'})}
                                            </span>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">{t('_.created_at', {defaultValue: 'Created At'})}</p>
                                            <p className="text-xs text-gray-700 font-bold mt-0.5">{secretKey.createdAt}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function ApiKeyField({value}) {
    const {t} = useTranslation();
    const [visible, setVisible] = useState(false);

    return (
        <div className="flex items-center gap-2">
            <span
                className={`font-mono text-sm font-bold bg-gray-50 text-gray-800 px-3 py-1.5 rounded-lg border border-gray-200 break-all shadow-sm ${visible ? 'select-all' : 'filter blur-sm select-none'}`}
                onClick={() => setVisible(true)}
                title={visible ? t('_.click_to_copy', {defaultValue: 'Click to copy'}) : t('_.click_to_reveal', {defaultValue: 'Click to reveal'})}
            >
                {value}
            </span>
            <button
                type="button"
                onClick={() => setVisible(v => !v)}
                className="text-xs font-bold text-cyan-700 hover:text-cyan-800 underline"
            >
                {visible ? t('_.hide', {defaultValue: 'Hide'}) : t('_.reveal', {defaultValue: 'Reveal'})}
            </button>
        </div>
    );
}

export default Setting;

